import { useQuery } from "@tanstack/react-query";
import { createFileRoute, Link } from "@tanstack/react-router";
import { ArrowRight, Loader2, MessagesSquare, Plug, RefreshCw } from "lucide-react";
import { PageHeader } from "@/components/domain/misc";
import { Page } from "@/components/layout/page";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/misc";
import { HomeViewSwitch, type HomeView, useHomeView } from "@/features/home/home-view";
import { OnboardingChecklist } from "@/features/onboarding/checklist";
import { ProductHome } from "@/features/home/product-home";
import { ReliabilityOverview, ReliabilitySkeleton } from "@/features/overview/reliability-overview";
import { useMyRole } from "@/features/accounts/shared";
import { q, useRefreshAnalytics } from "@/lib/api";

export const Route = createFileRoute("/_app/")({
  component: HomePage,
});

const DESCRIPTION: Record<HomeView, string> = {
  product: "What users ask your agents for, whether they succeed, and what changed this week.",
  engineering: "Reliability, open issues and signals across all production agents.",
  risk: "Coverage, limits and exposure across all production agents.",
};

function HomePage() {
  const org = useQuery(q.org());
  const [view, setView] = useHomeView();
  const name = org.data?.name;
  const role = useMyRole();
  const refresh = useRefreshAnalytics();
  const canRefresh = role === "owner" || role === "admin";
  return (
    <Page>
      <PageHeader
        title="Home"
        description={view ? (name ? `${name} · ${DESCRIPTION[view]}` : DESCRIPTION[view]) : " "}
        actions={
          <>
            {view === "product" ? (
              <>
                {canRefresh ? (
                  <Button variant="ghost" size="sm" disabled={refresh.isPending} onClick={() => refresh.mutate()} title="Rebuild today's analytics now and rediscover use cases (owners and admins; at most every 10 minutes)">
                    {refresh.isPending ? <Loader2 className="animate-spin" /> : <RefreshCw />} Refresh analytics
                  </Button>
                ) : null}
                <Button variant="ghost" size="sm" asChild>
                  <Link to="/sessions">
                    <MessagesSquare /> Sessions
                  </Link>
                </Button>
              </>
            ) : view === "risk" ? (
              <Button variant="ghost" size="sm" asChild>
                <Link to="/coverage">
                  Policies & limits <ArrowRight />
                </Link>
              </Button>
            ) : (
              <Button variant="ghost" size="sm" asChild>
                <Link to="/setup">
                  <Plug /> Connect an agent
                </Link>
              </Button>
            )}
            {view ? <HomeViewSwitch value={view} onChange={setView} /> : <Skeleton className="h-8 w-64" />}
          </>
        }
      />
      <OnboardingChecklist dismissible className="mb-4" />
      {view === "product" ? <ProductHome /> : view ? <ReliabilityOverview variant={view} /> : <ReliabilitySkeleton />}
    </Page>
  );
}
